// Robust Typing Indicator Hook - Direct RoomMemberEvent.Typing Implementation
// Uses native Matrix typing events for reliable room-specific typing indicators

import { useEffect, useState } from 'react';
import { RoomMemberEvent } from 'matrix-js-sdk';
import { useI18n } from '../context/I18nContext';
import { useMatrixClient } from '../context/MatrixContext';

/**
 * Typing Indicator Hook
 * 
 * Uses RoomMemberEvent.Typing events which provide:
 * - Direct room context (no inference needed)
 * - Per-user typing events 
 * - Clean boolean typing state
 * - Native Matrix protocol support
 * 
 * This approach eliminates the need for complex room inference logic.
 */
export const useTypingIndicator = (roomId) => {
  const [typingUsers, setTypingUsers] = useState(new Map()); // userId -> typing state
  const client = useMatrixClient();
  const { t } = useI18n();

  useEffect(() => {
    // If there's no room or client, ensure the state is clear and do nothing.
    if (!roomId || !client) {
      setTypingUsers(new Map());
      return;
    }

    const currentUserId = client.getUserId();

    // Listen to individual user typing events using matrixService abstraction
    const handleRoomMemberTyping = (_event, member) => {
      const userId = member.userId;
      const isTyping = member.typing;
      
      // Skip our own typing events
      if (userId === currentUserId) return;

      // Update typing state for this specific user
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        
        if (isTyping) {
          // User started typing - add to map with user info
          const room = client.getRoom(roomId);
          const roomMember = room?.getMember(userId);
          
          newMap.set(userId, {
            userId,
            displayName: roomMember?.name || userId,
            isTyping: true,
            timestamp: Date.now(),
          });
        } else {
          // User stopped typing - remove from map
          newMap.delete(userId);
        }
        
        return newMap;
      });
    };

    // The logic from matrixService.addTypingListener is now inside the hook.
    const roomMemberTypingListener = (event, member) => {
      if (member.roomId === roomId) handleRoomMemberTyping(event, member);
    };
    client.on(RoomMemberEvent.Typing, roomMemberTypingListener);

    // Cleanup function: runs when roomId changes or the component unmounts.
    return () => {
      client.off(RoomMemberEvent.Typing, roomMemberTypingListener);
      setTypingUsers(new Map()); // Reset the typing users state for the new room.
    };
  }, [roomId, client]);

  // Convert typing users map to display text
  const formatTypingText = () => {
    const typingUsersList = Array.from(typingUsers.values());
    
    if (typingUsersList.length === 0) return '';

    if (typingUsersList.length === 1) {
      return t('ComposingIndicator.contactIsTyping', { 
        contactName: typingUsersList[0].displayName,
      });
    }

    if (typingUsersList.length === 2) {
      // Two users: "John & Jane are typing..."
      const nameList = typingUsersList.map(u => u.displayName).join(' & ');
      return t('ComposingIndicator.areTyping', { nameList });
    }

    // More than 2 users: "John, Jane & 2 others are typing..."
    const firstTwo = typingUsersList.slice(0, 2);
    const remainingCount = typingUsersList.length - 2;
    const othersText = t('ComposingIndicator.other', { numOther: remainingCount });
    const nameList = `${firstTwo.map(u => u.displayName).join(', ')} & ${othersText}`;
    
    return t('ComposingIndicator.areTyping', { nameList });
  };

  return {
    typingText: formatTypingText(),
    typingUsers: Array.from(typingUsers.values()),
    typingCount: typingUsers.size,
  };
};

export default useTypingIndicator;
